/*

  Statsdisplay.ts

  User status display component

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { ComponentFunc, m, VnodeChildren } from "./mithril";
import { ts } from "./i18n";
import { glyph, nbsp, valueOrK } from "./util";

interface IAttributes {
  view: IView;
  id: string;
  ownStats: Record<string, any>;
}

export const StatsDisplay: ComponentFunc<IAttributes> = () => {
  // Display key statistics, provided via the ownStats attribute

  let sel = 1;

  function vwStat(val?: number, icon?: string, suffix?: string): VnodeChildren {
    // Display a user statistics figure, eventually with an icon
    var txt = (val === undefined) ? "" : valueOrK(val);
    if (suffix !== undefined)
      txt += suffix;
    return icon ? [glyph(icon), nbsp(), txt] : txt;
  }

  return {

    view: (vnode) => {

      // Display statistics about this user
      var s = vnode.attrs.ownStats;
      var winRatio = 0, winRatioHuman = 0;
      var avgScore = 0, avgScoreHuman = 0;
      if (s !== undefined && s.games !== undefined && s.human_games !== undefined) {
        if (s.games > 0) {
          winRatio = Math.round(100.0 * s.wins / s.games);
          avgScore = Math.round(s.score / s.games);
        }
        if (s.human_games > 0) {
          winRatioHuman = Math.round(100.0 * s.human_wins / s.human_games);
          avgScoreHuman = Math.round(s.human_score / s.human_games);
        }
      }

      return m("div", { id: vnode.attrs.id },
        [
          m(".toggler",
            {
              id: 'own-toggler',
              title: ts("stats_choice"), // "With or without robot games"
              onclick: (ev: Event) => { sel = 3 - sel; ev.preventDefault(); },
            },
            [
              m(".option.small" + (sel === 1 ? ".selected" : ""),
                { id: 'opt1' },
                glyph("user")
              ),
              m(".option.small" + (sel === 2 ? ".selected" : ""),
                { id: 'opt2' },
                glyph("cog")
              )
            ]
          ),
          sel === 1 ? m("div",
            { id: 'own-stats-human', className: 'stats-box', style: { display: "inline-block" } },
            [
              m(".stats-fig", { title: ts('Elo-stig') },
                s ? vwStat(s.locale_elo?.human_elo, "crown") : ""),
              m(".stats-fig.stats-games", { title: ts('Fjöldi viðureigna') },
                s ? vwStat(s.human_games, "th") : ""),
              m(".stats-fig.stats-win-ratio", { title: ts('Vinningshlutfall') },
                vwStat(winRatioHuman, "bookmark", "%")),
              m(".stats-fig.stats-avg-score", { title: ts('Meðalstigafjöldi') },
                vwStat(avgScoreHuman, "dashboard"))
            ]
          ) : "",
          sel === 2 ? m("div",
            { id: 'own-stats-all', className: 'stats-box', style: { display: "inline-block" } },
            [
              m(".stats-fig", { title: ts("Elo-stig") },
                s ? vwStat(s.locale_elo?.elo, "crown") : ""),
              m(".stats-fig.stats-games", { title: ts('Fjöldi viðureigna') },
                s ? vwStat(s.games, "th") : ""),
              m(".stats-fig.stats-win-ratio", { title: ts('Vinningshlutfall') },
                vwStat(winRatio, "bookmark", "%")),
              m(".stats-fig.stats-avg-score", { title: ts('Meðalstigafjöldi') },
                vwStat(avgScore, "dashboard"))
            ]
          ) : ""
        ]
      );
    }

  };
};
